/**
 * Microsoft Clarity — session recording / heatmaps (frontend only).
 *
 * Gated on production + project id. Respects Save-Data (fe-performance.mdc §11.4).
 * Masking mode is Relaxed in the Clarity UI; explicit data-clarity-mask targets
 * cover recovery secrets and copyable tokens. Sentry Session Replay is NOT used.
 */

import Clarity from '@microsoft/clarity';

const DEFAULT_ENVIRONMENT = 'development';

type ClarityWindow = Window & {
  clarity?: (...args: unknown[]) => void;
};

type ClarityMetadata = {
  projectId?: string;
  userId?: string;
  sessionId?: string;
};

let clarityEnabled = false;
let clarityPaused = false;
let cachedSessionUrl: string | undefined;

/**
 * Paths where the recorder stays off: a token/secret may appear in the URL, or the privacy
 * policy promises no recording there (sign-in, password reset, invitation, MFA — see
 * `PrivacyPolicyContent`). Invitations land on `/set-password`.
 */
const TOKEN_BEARING_PATH_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/auth/sso/callback',
  '/mfa/verify',
  '/mfa/enroll',
] as const;

/** Heavy canvas viewers — pause the mutation observer (fe-performance.mdc §11). */
const HEAVY_VIEWER_PATH_PREFIXES = ['/pdf-viewer', '/dxf-viewer'] as const;

function readProjectId(): string | undefined {
  const id = import.meta.env.VITE_CLARITY_PROJECT_ID;
  return typeof id === 'string' && id.trim().length > 0 ? id.trim() : undefined;
}

/**
 * Clarity's own `environment` tag (`stage` / `production`). Deliberately not
 * `VITE_SENTRY_ENVIRONMENT`: the two tools are configured independently, so
 * turning Clarity on never depends on Sentry settings (FR-54).
 */
function readEnvironment(): string {
  const env = import.meta.env.VITE_CLARITY_ENVIRONMENT;
  if (typeof env === 'string' && env.trim().length > 0) return env.trim();
  return import.meta.env.PROD ? 'production' : DEFAULT_ENVIRONMENT;
}

function hasSaveDataPreference(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  if (connection?.saveData) return true;
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-reduced-data: reduce)').matches;
  }
  return false;
}

function normalizeAppPath(pathname: string): string {
  if (pathname.startsWith('/v2')) {
    const rest = pathname.slice('/v2'.length);
    return rest.length === 0 ? '/' : rest;
  }
  return pathname;
}

/**
 * True when the current (or given) path may carry auth tokens in the URL.
 */
export function isClarityBlockedPath(pathname: string = window.location.pathname): boolean {
  const path = normalizeAppPath(pathname);
  return TOKEN_BEARING_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * True for DXF / PDF viewer routes where recording cost is high and value is low.
 */
export function isClarityHeavyViewerPath(
  pathname: string = window.location.pathname,
): boolean {
  const path = normalizeAppPath(pathname);
  return HEAVY_VIEWER_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

function callClarityCommand(...args: unknown[]): void {
  const clarityFn = (window as ClarityWindow).clarity;
  if (typeof clarityFn === 'function') {
    clarityFn(...args);
  }
}

function refreshSessionUrlCache(): void {
  callClarityCommand(
    'metadata',
    (metadata: ClarityMetadata) => {
      if (metadata?.projectId && metadata?.userId && metadata?.sessionId) {
        cachedSessionUrl = `https://clarity.microsoft.com/player/${metadata.projectId}/${metadata.userId}/${metadata.sessionId}`;
      }
    },
    // wait=true — defer the callback until the session exists. With wait=false the
    // callback fires before Clarity has a sessionId, so the cache never populates
    // and the first Sentry event ships without a claritySessionUrl tag.
    true,
  );
}

/**
 * Initialize Clarity once in production builds with a project id.
 * No-ops in dev, Playwright, Save-Data, and token-bearing landing URLs.
 *
 * A token-bearing landing URL only *defers* startup — `syncClarityForPath` retries
 * on the first clean navigation. Bailing permanently here would mean every Entra/SSO
 * user (who always lands on /auth/sso/callback) is never recorded at all.
 */
export function initClarity(pathname?: string): void {
  if (clarityEnabled) return;
  if (!import.meta.env.PROD) return;
  if (hasSaveDataPreference()) return;

  const projectId = readProjectId();
  if (!projectId) return;
  if (typeof window !== 'undefined' && isClarityBlockedPath(pathname)) return;

  Clarity.init(projectId);
  Clarity.setTag('environment', readEnvironment());
  clarityEnabled = true;
  clarityPaused = false;
  refreshSessionUrlCache();
}

function pauseRecorder(): void {
  if (clarityPaused) return;
  callClarityCommand('pause');
  clarityPaused = true;
}

function resumeRecorder(): void {
  if (!clarityPaused) return;
  callClarityCommand('resume');
  clarityPaused = false;
}

/**
 * Pause / resume the recorder based on the current route. Token-bearing routes and
 * heavy canvas viewers pause; everything else resumes.
 *
 * Deliberately uses `pause`/`resume` rather than `stop`. `stop` tears down every
 * clarity-js module and clears the upload queue, and it is NOT reversible here:
 * restarting needs a fresh `start`, but the npm wrapper's `injectScript` early-returns
 * once `#clarity-script` exists. Using `stop` therefore killed recording for the rest
 * of the session the first time a user hit /mfa/verify — i.e. almost every login.
 */
export function syncClarityForPath(pathname: string): void {
  if (isClarityBlockedPath(pathname)) {
    pauseRecorder();
    return;
  }

  // Retry startup — the landing URL may have been token-bearing (SSO callback).
  if (!clarityEnabled) initClarity(pathname);
  if (!clarityEnabled) return;

  if (isClarityHeavyViewerPath(pathname)) {
    pauseRecorder();
    return;
  }

  resumeRecorder();
}

/**
 * Identify the current user for Clarity. Passes numeric userId only — never email
 * as friendlyName (Clarity stores friendlyName in plaintext).
 */
export function identifyClarity(input: {
  userId: number;
  role?: string;
  companyId?: number | null;
  impersonating?: boolean;
  isSSI?: boolean;
}): void {
  // Runs while paused too: the session hydrates on /auth/sso/callback and /mfa/verify,
  // so skipping identity here would leave those sessions permanently unattributed.
  if (!clarityEnabled) return;

  Clarity.identify(String(input.userId));
  if (input.role) Clarity.setTag('role', input.role);
  if (input.companyId != null) Clarity.setTag('companyId', String(input.companyId));
  Clarity.setTag('impersonating', input.impersonating ? 'true' : 'false');
  Clarity.setTag('isSSI', input.isSSI ? 'true' : 'false');
  Clarity.setTag('environment', readEnvironment());
  refreshSessionUrlCache();
}

/** Clear identity tags on logout (Clarity has no formal logout — overwrite tags). */
export function clearClarityIdentity(): void {
  if (!clarityEnabled) return;
  Clarity.setTag('impersonating', 'false');
  Clarity.setTag('isSSI', 'false');
}

/** Latest known Clarity player URL for Sentry cross-linking. */
export function getClaritySessionUrl(): string | undefined {
  if (!cachedSessionUrl) refreshSessionUrlCache();
  return cachedSessionUrl;
}

/** Tag the current Clarity session with a Sentry event id. */
export function tagClarityWithSentryEventId(eventId: string): void {
  if (!clarityEnabled) return;
  Clarity.setTag('sentryEventId', eventId);
}

/** Test-only reset. */
export function __resetClarityForTests(): void {
  clarityEnabled = false;
  clarityPaused = false;
  cachedSessionUrl = undefined;
}

/** Test-only — mark Clarity as initialized without loading the script. */
export function __forceEnableClarityForTests(): void {
  clarityEnabled = true;
  clarityPaused = false;
}

// Side-effect init when this module is first evaluated (via main.tsx or sentry.ts).
initClarity();
